import React from 'react';
import { FeaturePageLayout } from '@/components/shared/FeaturePageLayout';
import { Headphones } from 'lucide-react';

export default function SupportAutomationPage() {
  return (
    <FeaturePageLayout
      icon={Headphones}
      title="Support Automation"
      badge="Automation Feature"
      description="Deliver 24/7 customer support on WhatsApp without expanding your team. Your AI agent handles complaints, troubleshooting, and service requests — and knows when to bring in a human for complex cases."
      capabilities={[
        {
          title: '24/7 Availability',
          description: 'Your AI support agent never sleeps. Customers get help at midnight, on weekends, and during holidays — always responsive.',
        },
        {
          title: 'Complaint Resolution',
          description: 'The AI acknowledges issues, collects details, and guides customers through resolution steps or creates support tickets.',
        },
        {
          title: 'Human Handoff',
          description: 'When a situation requires empathy or complex decision-making, the AI seamlessly transfers to a live agent with full context.',
        },
        {
          title: 'Ticket Creation',
          description: 'Automatically create support tickets from conversations. Track status, priority, and resolution time from your dashboard.',
        },
        {
          title: 'Satisfaction Tracking',
          description: 'After issue resolution, the AI can request CSAT ratings to help you measure and improve your service quality.',
        },
        {
          title: 'Response Templates',
          description: 'Pre-configured response flows for common support scenarios — refunds, exchanges, service issues, and technical problems.',
        },
      ]}
      howItWorks={[
        {
          title: 'Define Support Scenarios',
          description: 'Set up common support flows — order issues, product complaints, service requests. The AI learns your resolution steps.',
        },
        {
          title: 'AI Handles Incoming Requests',
          description: 'Customers message on WhatsApp with their issues. The AI identifies the problem type and begins resolution.',
        },
        {
          title: 'Resolve or Escalate',
          description: 'Simple issues are resolved immediately. Complex cases are escalated to human agents with full conversation context.',
        },
        {
          title: 'Track & Improve',
          description: 'Monitor resolution rates, response times, and satisfaction scores. Continuously improve your support automation.',
        },
      ]}
      useCases={[
        { emoji: '📦', title: 'Order Issues', description: 'Handle missing orders, wrong items, and delivery complaints with automated resolution flows.' },
        { emoji: '🔧', title: 'Technical Support', description: 'Guide customers through troubleshooting steps for products or services before escalating.' },
        { emoji: '💰', title: 'Refund Requests', description: 'Process refund requests, collect required information, and initiate refunds automatically.' },
        { emoji: '📅', title: 'Appointment Changes', description: 'Handle rescheduling, cancellations, and booking modifications without human intervention.' },
        { emoji: '⚠️', title: 'Service Outages', description: 'Proactively notify customers about issues and handle incoming queries during outages.' },
        { emoji: '📝', title: 'Feedback Collection', description: 'Gather detailed feedback after interactions to improve your products and services over time.' },
      ]}
      ctaText="Automate your customer support on WhatsApp"
    />
  );
}
